import React, { FC, useEffect, useRef, useState } from "react";
import {
  Chip,
  IconButton,
  List,
  ListItem,
  ListItemSecondaryAction,
  ListItemText,
  TextField,
  ThemeProvider,
  Theme,
  StyledEngineProvider,
  Tooltip,
} from "@mui/material";
import { ControllerFieldState, ControllerRenderProps } from "react-hook-form/dist/types/controller";
import { useMetadataByCategory } from "components/summary/hooks";
import { Autocomplete } from "@mui/material";
import AutocompleteCollapsableGroup, {
  OptionComponent,
  OtherComponent,
} from "components/app/AutocompleteCollapsableGroup";
import { createPortal } from "react-dom";
import { Clear } from "@mui/icons-material";
import { lightTheme } from "theme";

declare module "@mui/styles/defaultTheme" {
  // eslint-disable-next-line
  interface DefaultTheme extends Theme {}
}

export interface TypeOption {
  name: string;
  value: string;
  type?: string;
}

type BaseMultipleAutocompleteProps = {
  field: ControllerRenderProps;
  fieldState: ControllerFieldState;
} & {
  disabled?: boolean;
  showMarginLeft?: boolean;
  category: string;
  fullWidth?: boolean;
  limitTags?: number;
  closeCallback?: () => Promise<void>;
  minWidth?: number;
  id?: string;
};

const BaseMultipleAutocomplete: FC<BaseMultipleAutocompleteProps> = ({
  fieldState,
  field,
  disabled,
  showMarginLeft = false,
  category,
  fullWidth,
  limitTags = 1,
  closeCallback,
  minWidth,
  id = "add-other-id",
}) => {
  const { data } = useMetadataByCategory(category);
  const [value, setValue] = useState<TypeOption[]>(field.value);
  const [inputValue, setInputValue] = useState("");
  const [options, setOptions] = useState<TypeOption[]>([]);
  const [portal, setPortal] = useState<
    | {
        id: string;
        option?: TypeOption;
        callback?: (edit: boolean) => void;
      }
    | null
    | undefined
  >(undefined);
  const [open, setOpen] = useState(true);

  const onChange = (_e: any, value: TypeOption[]) => {
    setValue(value);
  };

  useEffect(() => {
    field.onChange(value);
  }, [value]);

  // when open, show the add other input, because using portal need to let the dom element render first
  useEffect(() => {
    if (open)
      setTimeout(() => {
        setPortal({ id });
      }, 500);
  }, [open]);

  const onOpen = () => setOpen(true);
  const onClose = async (event: any) => {
    if (
      (event?.relatedTarget as HTMLElement)?.className?.includes?.("block-close") ||
      (event?.target as HTMLElement)?.parentElement?.className?.includes?.("block-close") ||
      (event?.target as HTMLElement)?.className?.includes?.("block-close")
    ) {
    } else {
      setOpen(false);
      await closeCallback?.();
    }
  };

  const generateOption = () => {
    if (data) {
      const options = data
        .filter((x) => x.code !== "OTHER")
        .map((x) => ({
          name: x.value,
          value: x.value,
          type: "Standard",
        }));
      options.push({ type: "Other", name: "", value: "" });
      const otherValues = field.value?.filter((x: TypeOption) => x.type === "Other");
      if (otherValues.length) {
        options.push(...otherValues);
      }
      return options;
    }
    return [];
  };

  const inputRef = useRef<HTMLInputElement>();

  useEffect(() => {
    if (portal === null) {
      inputRef?.current?.focus();
      setPortal({ id });
    }
  }, [portal]);

  useEffect(() => {
    setOptions(generateOption());
  }, [data]);

  return (
    <StyledEngineProvider injectFirst>
      <ThemeProvider theme={lightTheme}>
        <div
          style={{
            marginTop: 8,
            flexBasis: fullWidth ? undefined : "49%",
            marginLeft: showMarginLeft ? "2%" : 0,
            width: fullWidth ? "100%" : undefined,
          }}
        >
          <Autocomplete
            {...field}
            open={open}
            filterOptions={(o: TypeOption[], s) =>
              o.filter((x) => x.value.toLowerCase()?.includes(s.inputValue?.toLowerCase()))
            }
            onOpen={onOpen}
            onClose={onClose}
            classes={{
              option: "listbox-option",
            }}
            disabled={disabled}
            disableCloseOnSelect={true}
            getOptionLabel={(option) => option.name}
            loading={data === undefined}
            blurOnSelect={false}
            clearOnBlur={false}
            multiple={true}
            limitTags={limitTags}
            size={"small"}
            isOptionEqualToValue={(o, v) => o.value === v.value}
            options={options}
            groupBy={(o) => o.type}
            renderGroup={(p) => (
              <AutocompleteCollapsableGroup
                {...p}
                inputValue={inputValue}
                multiple={true}
                hideHeader={true}
                id={id}
                actions={{
                  setOptions,
                  setValue,
                  setOpen,
                  setInputValue,
                  setPortal,
                }}
              />
            )}
            renderOption={(props, o, s) => (
              <li {...props}>
                <OptionComponent
                  option={o}
                  multiple={true}
                  state={s}
                  actions={{
                    setOptions,
                    setValue,
                    setInputValue,
                    setPortal,
                  }}
                />
              </li>
            )}
            inputValue={inputValue}
            onInputChange={(_, value) => setInputValue(value)}
            renderTags={(value, getTagProps) => {
              return (
                <>
                  {value.slice(0, limitTags).map((x, i) => (
                    <Chip
                      key={x.value}
                      label={x.name}
                      {...getTagProps({ index: i })}
                      size={"small"}
                      style={{ maxWidth: 80, margin: "0 2px" }}
                    />
                  ))}

                  {value.length - limitTags > 0 ? (
                    <Tooltip
                      title={
                        <List
                          disablePadding={true}
                          dense={true}
                        >
                          {value.slice(limitTags).map((x, index) => (
                            <ListItem
                              dense={true}
                              key={x.value}
                            >
                              <ListItemText primary={x.name} />
                              <ListItemSecondaryAction style={{ right: 4 }}>
                                <IconButton
                                  size={"small"}
                                  onClick={
                                    disabled
                                      ? undefined
                                      : (
                                          getTagProps({
                                            index: index + limitTags,
                                          }) as any
                                        )?.onDelete
                                  }
                                >
                                  <Clear />
                                </IconButton>
                              </ListItemSecondaryAction>
                            </ListItem>
                          ))}
                        </List>
                      }
                    >
                      <Chip
                        size={"small"}
                        label={`${value.length - limitTags} more`}
                      />
                    </Tooltip>
                  ) : null}
                </>
              );
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                margin="normal"
                variant="outlined"
                classes={{ root: "dropdown-multiple-option" }}
                InputProps={{
                  ...params.InputProps,
                  type: "search",
                  style: {
                    width: showMarginLeft ? "100%" : "98%",
                    marginTop: -15,
                    background: "#fff",
                    flexWrap: "nowrap",
                    maxWidth: fullWidth ? undefined : 266.55,
                    minWidth,
                  },
                }}
                error={!!fieldState.error}
                helperText={fieldState.error?.message}
                size={"small"}
                placeholder={field.value?.length ? "" : "Please select"}
                inputRef={inputRef}
                fullWidth={fullWidth}
                autoFocus={true}
              />
            )}
            onChange={onChange}
            value={field.value ?? value}
          />
        </div>
        {portal &&
          document.getElementById(portal.id) &&
          createPortal(
            <OtherComponent
              defaultValue={portal.option?.value || ""}
              actions={{
                setOptions,
                setValue,
                setPortal,
              }}
              validWithPrefix={true}
              type={portal.option?.value ? "update" : "create"}
              multiple={true}
              prefixNameWithOther={true}
            />,
            document.getElementById(portal.id)!
          )}
      </ThemeProvider>
    </StyledEngineProvider>
  );
};

export default BaseMultipleAutocomplete;
